/**
 * Authentication Service
 *
 * Provides centralized management of auth tokens.
 * Uses sessionStorage for secure storage (cleared when browser tab closes).
 *
 * Hybrid Approach:
 * - Extracts auth token from URL on initial page load
 * - Stores token securely in sessionStorage
 * - Provides token for API calls via getToken()
 * - Future: Can be updated to use Authorization headers when backend supports it
 */

const AUTH_STORAGE_KEY = 'booking_auth_token';

interface AuthService {
  setToken(token: string): void;
  getToken(): string | null;
  hasToken(): boolean;
  clearToken(): void;
  getAuthHeaders(): Record<string, string>;
  extractAndStore(authFromUrl: string): boolean;
}

const authService: AuthService = {
  /**
   * Store the auth token securely
   */
  setToken(token: string): void {
    if (token) {
      sessionStorage.setItem(AUTH_STORAGE_KEY, token);
    }
  },

  /**
   * Retrieve the stored auth token
   */
  getToken(): string | null {
    return sessionStorage.getItem(AUTH_STORAGE_KEY);
  },

  /**
   * Check if an auth token is stored
   */
  hasToken(): boolean {
    return !!this.getToken();
  },

  /**
   * Clear the stored auth token
   */
  clearToken(): void {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  },

  /**
   * Get authorization headers for API calls
   * Note: Currently not used as backend expects auth in URL path
   * Ready for future migration to header-based auth
   */
  getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  },

  /**
   * Extract auth token from URL parameters and store it
   */
  extractAndStore(authFromUrl: string): boolean {
    if (authFromUrl) {
      this.setToken(authFromUrl);
      return true;
    }
    return false;
  }
};

export default authService;
